import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Flourish } from "@/components/ui/Flourish";
import { Petalas } from "@/components/aniversario/Petalas";
import { MensagemEntrada } from "@/components/para-sempre/MensagemEntrada";
import type { MessageWithSrc as MensagemComSrc } from "@historia/shared/contracts";

import type { PhotoSummary as FotoResumo } from "@historia/shared/contracts";

/** A carta de aniversário, passada pelo servidor (texto fica fixo no código). */
export interface CartaAniversario {
  /** Já chegou (ou passou) o 21 de junho? */
  chegou: boolean;
  /** Dias em falta (para o teaser). */
  diasEmFalta: number;
  /** Ordinal já formatado ("primeiro", "2.º"…). */
  ordinal: string;
  /** Foto em destaque assinada (no dia). */
  fotoSrc: string | null;
  fotoLegenda: string | null;
  /** Textos da carta (de lib/conteudo.ts). */
  kicker: string;
  saudacao: string;
  paragrafos: string[];
  assinatura: string;
  rodape: string;
  teaserKicker: string;
  teaserTitulo: string;
  teaserTexto: string;
}

/**
 * /para-sempre como um livro: uma página de cada vez, vira-se com as setas.
 * Página 1 = a carta de aniversário (teaser antes do dia, cena completa depois);
 * páginas 2…N = as mensagens da coleção. (IDEA-013 v2)
 */
export function LivroParaSempre({
  carta,
  mensagens,
  fotos,
}: {
  carta: CartaAniversario;
  mensagens: MensagemComSrc[];
  fotos: FotoResumo[];
}) {
  const reduzir = useReducedMotion();
  // Página 0 = carta; 1..N = mensagens[0..N-1].
  const totalPaginas = mensagens.length + 1;
  const [pagina, setPagina] = useState(0);
  // Direção da transição (1 = avançar, -1 = recuar) para a animação.
  const [direcao, setDirecao] = useState(1);

  // Se as mensagens encolherem (ex.: apagar a última), não fica fora do limite.
  useEffect(() => {
    if (pagina > totalPaginas - 1) setPagina(totalPaginas - 1);
  }, [pagina, totalPaginas]);

  function irPara(n: number) {
    if (n < 0 || n > totalPaginas - 1) return;
    setDirecao(n > pagina ? 1 : -1);
    setPagina(n);
  }

  const variantes = {
    entra: (dir: number) => ({ opacity: 0, x: reduzir ? 0 : dir * 40 }),
    centro: { opacity: 1, x: 0 },
    sai: (dir: number) => ({ opacity: 0, x: reduzir ? 0 : dir * -40 }),
  };

  return (
    <div className="relative">
      {/* As pétalas acompanham a cena da carta (no dia). */}
      {pagina === 0 && carta.chegou && <Petalas />}

      <div className="relative z-10 min-h-[60dvh]">
        <AnimatePresence mode="wait" custom={direcao}>
          <motion.div
            key={pagina}
            custom={direcao}
            variants={variantes}
            initial="entra"
            animate="centro"
            exit="sai"
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          >
            {pagina === 0 ? (
              <PaginaCarta carta={carta} />
            ) : (
              <MensagemEntrada
                mensagem={mensagens[pagina - 1]}
                fotos={fotos}
                indice={pagina - 1}
                total={mensagens.length}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Controlos de navegação do livro */}
      {totalPaginas > 1 && (
        <nav className="relative z-10 mt-8 flex items-center justify-center gap-4">
          <BotaoVirar
            direcao="anterior"
            aoClicar={() => irPara(pagina - 1)}
            desativado={pagina === 0}
          />
          <ol className="flex items-center gap-2">
            {Array.from({ length: totalPaginas }, (_, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => irPara(i)}
                  aria-label={i === 0 ? "Carta de aniversário" : `Mensagem ${i}`}
                  aria-current={i === pagina ? "true" : undefined}
                  className={`block h-2.5 w-2.5 rounded-full transition-colors ${
                    i === pagina ? "bg-ouro" : "bg-ouro/25 hover:bg-ouro/50"
                  }`}
                />
              </li>
            ))}
          </ol>
          <BotaoVirar
            direcao="seguinte"
            aoClicar={() => irPara(pagina + 1)}
            desativado={pagina === totalPaginas - 1}
          />
        </nav>
      )}

      <p className="relative z-10 mt-3 text-center text-xs text-tinta-700/60">
        {pagina === 0 ? "Carta de aniversário" : `Mensagem ${pagina}`} · {pagina + 1} /{" "}
        {totalPaginas}
      </p>
    </div>
  );
}

function BotaoVirar({
  direcao,
  aoClicar,
  desativado,
}: {
  direcao: "anterior" | "seguinte";
  aoClicar: () => void;
  desativado: boolean;
}) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      disabled={desativado}
      aria-label={direcao === "anterior" ? "Página anterior" : "Página seguinte"}
      className="flex h-11 w-11 items-center justify-center rounded-full border border-ouro/30 bg-creme-50 text-xl text-ouro-600 shadow-sm transition-colors hover:bg-creme-100 disabled:cursor-not-allowed disabled:opacity-30"
    >
      {direcao === "anterior" ? "‹" : "›"}
    </button>
  );
}

/** Página 1 do livro: a carta de aniversário (teaser ou cena completa). */
function PaginaCarta({ carta }: { carta: CartaAniversario }) {
  // Antes do dia: teaser tenro + contagem decrescente.
  if (!carta.chegou) {
    return (
      <section className="flex flex-col items-center text-center">
        <p className="font-display text-lg italic text-ouro-600">{carta.teaserKicker}</p>
        <h2 className="mt-2 font-display text-3xl text-tinta sm:text-4xl">{carta.teaserTitulo}</h2>
        <div className="my-6">
          <Flourish />
        </div>
        <p className="max-w-prose leading-relaxed text-tinta-700">{carta.teaserTexto}</p>
        <div className="mt-10 flex flex-col items-center gap-1">
          <span className="font-display text-6xl text-ouro">{carta.diasEmFalta}</span>
          <span className="text-sm uppercase tracking-wide text-tinta-700/70">
            {carta.diasEmFalta === 1 ? "dia que falta" : "dias que faltam"}
          </span>
        </div>
      </section>
    );
  }

  // No dia (e depois): a cena completa — foto à esquerda, carta à direita (como um livro).
  return (
    <section>
      <header className="text-center">
        <p className="font-display text-lg italic text-ouro-600">{carta.kicker}</p>
        <h2 className="mt-2 font-display text-3xl text-tinta sm:text-4xl">
          O nosso {carta.ordinal} aniversário
        </h2>
        <p className="mt-2 text-lg text-tinta-700">{carta.saudacao}</p>
        <div className="my-6">
          <Flourish />
        </div>
      </header>

      <div className="grid items-start gap-8 md:grid-cols-2">
        {carta.fotoSrc ? (
          <figure className="md:sticky md:top-24">
            <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-ouro/20 shadow-lg">
              <img
                src={carta.fotoSrc}
                alt={carta.fotoLegenda ?? "A nossa fotografia"}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            {carta.fotoLegenda && (
              <figcaption className="mt-2 text-center text-sm italic text-tinta-700/80">
                {carta.fotoLegenda}
              </figcaption>
            )}
          </figure>
        ) : (
          <span aria-hidden />
        )}

        <article className="rounded-3xl border border-ouro/15 bg-creme-50/80 px-6 py-8 text-left shadow-sm sm:px-8 sm:py-9">
          {carta.paragrafos.map((paragrafo, i) => {
            if (i === 0) {
              return (
                <p key={i} className="mb-5 font-display text-2xl italic text-ouro-600">
                  {paragrafo}
                </p>
              );
            }
            const ultimo = i === carta.paragrafos.length - 1;
            return (
              <p
                key={i}
                className={
                  ultimo
                    ? "mt-6 font-display text-xl italic text-ouro-600"
                    : "mb-5 leading-loose text-tinta-700 last:mb-0"
                }
              >
                {paragrafo}
              </p>
            );
          })}
          <p className="mt-8 text-center font-display text-xl italic text-ouro-600">
            {carta.assinatura}
          </p>
        </article>
      </div>

      <p className="mt-8 text-center text-sm text-tinta-700/70">{carta.rodape}</p>
    </section>
  );
}
